import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner, Alert, Form } from "react-bootstrap";
import api from "../../services/api";

const TermosAcceptModal = ({
  show,
  termos,
  onAccept,
  onDecline,
  termosDinamicos = [],
  termosDinamicosTitulo = "",
  prestadorUuid = null,
  programaUuid = null,
  respostasIniciais = {},
  labelAceiteTermos = null,
}) => {
  const [currentTermoIndex, setCurrentTermoIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [acceptedCurrent, setAcceptedCurrent] = useState(false);
  const [respostasDinamicas, setRespostasDinamicas] = useState(respostasIniciais || {});
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    if (show) {
      setCurrentTermoIndex(0);
      setAcceptedCurrent(false);
      setError(null);
      setRespostasDinamicas(respostasIniciais || {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, termos]);

  const currentTermo =
    termos.length > currentTermoIndex ? termos[currentTermoIndex] : null;

  const isUltimoTermo = currentTermoIndex >= termos.length - 1;
  const temTermosDinamicos = Array.isArray(termosDinamicos) && termosDinamicos.length > 0;

  const handleRespostaDinamicaChange = (slug, valor) => {
    setRespostasDinamicas((prev) => ({ ...prev, [slug]: valor }));
  };

  // Valida os termos dinâmicos do médico: regra de bloqueio (ex.: "Não autorizo" em consentimento
  // essencial) e obrigatoriedade. Retorna { ok, mensagem }.
  const validarTermosDinamicos = () => {
    if (!temTermosDinamicos) return { ok: true };

    for (const q of termosDinamicos) {
      if (!q?.slug) continue;
      const v = respostasDinamicas[q.slug];
      const rb = q.regra_bloqueio_cadastro || {};
      if (rb.ativo === true && v === rb.quando_valor) {
        return { ok: false, mensagem: rb.mensagem || "Não é possível prosseguir com as opções selecionadas." };
      }
    }

    for (const q of termosDinamicos) {
      if (!q?.slug) continue;
      const v = respostasDinamicas[q.slug];
      const vazio = v === undefined || v === null || v === "";
      if (q.obrigatorio && (vazio || (q.tipo === "checkbox" && v !== true))) {
        return { ok: false, mensagem: "Preencha os campos obrigatórios do formulário de aceite para continuar." };
      }
    }

    return { ok: true };
  };

  // Função chamada ao clicar em "Aceitar e Continuar"
  const handleAcceptCurrent = async () => {
    if (!currentTermo || !acceptedCurrent) return;

    // No último termo, valida os campos dinâmicos do médico antes de registrar qualquer aceite.
    if (isUltimoTermo && temTermosDinamicos) {
      const val = validarTermosDinamicos();
      if (!val.ok) {
        setError(val.mensagem);
        return;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      await api.post("/termos-plataforma/registrar-consentimento/", {
        termo_id: currentTermo.id,
        // termo que vale por indicação: sem isto o aceite entraria como termo comum e a
        // outra indicação da mesma pessoa nunca sairia da lista
        ...(currentTermo.indicacao_uuid ? { indicacao_uuid: currentTermo.indicacao_uuid } : {}),
        // O backend pega o 'usuario_id' do token JWT
      });

      // Se for o último termo, salva os termos dinâmicos (se houver) e finaliza
      if (isUltimoTermo) {
        if (temTermosDinamicos && prestadorUuid) {
          // Envia apenas as respostas das perguntas atualmente configuradas — respostas antigas
          // (de perguntas removidas da config) seriam rejeitadas pelo backend como "slug não configurado".
          const slugsAtuais = new Set((termosDinamicos || []).map((q) => q?.slug).filter(Boolean));
          const respostasFiltradas = Object.fromEntries(
            Object.entries(respostasDinamicas).filter(([slug]) => slugsAtuais.has(slug))
          );
          await api.put(
            `/pessoas/prestadores/uuid/${prestadorUuid}/`,
            {
              // data_aceite_termo é obrigatório para o backend processar e gravar os termos
              // dinâmicos no vínculo programa×prestador (handler PrestadorUUIDDetail.put).
              data_aceite_termo: new Date().toISOString(),
              programa_uuid: programaUuid,
              termos_dinamicos_respostas_json: respostasFiltradas,
            },
            { headers: { "X-USERNAME": import.meta.env.VITE_X_USERNAME } }
          );
        }
        onAccept();
      } else {
        // Se houver mais termos, avança para o próximo
        setCurrentTermoIndex((prev) => prev + 1);
        setAcceptedCurrent(false); // Reseta o checkbox
      }
    } catch (err) {
      console.error("Erro ao registrar consentimento:", err);
      let errMsg = "Erro ao registrar aceite.";
      if (err.response && err.response.data) {
        try {
          const errors = err.response.data;
          const messages = Object.keys(errors).map(
            (key) =>
              `${key}: ${
                Array.isArray(errors[key])
                  ? errors[key].join(", ")
                  : typeof errors[key] === "object"
                  ? JSON.stringify(errors[key])
                  : errors[key]
              }`
          );
          errMsg = messages.join("\n");
        } catch (e) {
          errMsg = JSON.stringify(err.response.data);
        }
      }
      setError(errMsg);

      if (err.response && err.response.status === 401) {
        onDecline();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalHide = () => {
    setError("É necessário aceitar todos os termos para continuar.");
  };

  // Renderiza uma pergunta dos termos dinâmicos do médico (radio / checkbox / textarea / date).
  const renderTermoDinamico = (campo) => {
    if (!campo?.slug) return null;
    const valor = respostasDinamicas[campo.slug];

    if (campo.tipo === "checkbox") {
      return (
        <div key={campo.slug} className="mt-3">
          <div className="mb-1"><strong>{campo.label || campo.slug}</strong></div>
          {campo.texto && <div className="text-muted mb-2">{campo.texto}</div>}
          <Form.Check
            type="checkbox"
            id={`term-din-${campo.slug}`}
            label={campo.checkbox_label || "Confirmo"}
            checked={Boolean(valor)}
            onChange={(e) => handleRespostaDinamicaChange(campo.slug, e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          {campo.observacao && <div className="mt-2 text-muted small">{campo.observacao}</div>}
        </div>
      );
    }

    if (campo.tipo === "radio") {
      const opcoes = Array.isArray(campo.opcoes) ? campo.opcoes : [];
      return (
        <div key={campo.slug} className="mt-3">
          <div className="mb-1"><strong>{campo.label || campo.slug}</strong></div>
          {campo.texto && <div className="text-muted mb-2">{campo.texto}</div>}
          {opcoes.map((opcao, idx) => {
            const opcaoValor = typeof opcao === "object" ? opcao?.valor : opcao;
            const opcaoLabel = typeof opcao === "object" ? (opcao?.label ?? String(opcaoValor)) : String(opcao);
            return (
              <Form.Check
                key={`${campo.slug}-${idx}`}
                type="radio"
                name={`term-din-${campo.slug}`}
                id={`term-din-${campo.slug}-${idx}`}
                label={opcaoLabel}
                checked={valor === opcaoValor}
                onChange={() => handleRespostaDinamicaChange(campo.slug, opcaoValor)}
              />
            );
          })}
          {campo.observacao && <div className="mt-2 text-muted small">{campo.observacao}</div>}
        </div>
      );
    }

    if (campo.tipo === "textarea") {
      return (
        <Form.Group key={campo.slug} className="mt-3">
          <Form.Label><strong>{campo.label || campo.slug}</strong></Form.Label>
          {campo.texto && <div className="text-muted mb-2">{campo.texto}</div>}
          <Form.Control
            as="textarea"
            rows={3}
            value={valor || ""}
            onChange={(e) => handleRespostaDinamicaChange(campo.slug, e.target.value)}
          />
          {campo.observacao && <div className="mt-2 text-muted small">{campo.observacao}</div>}
        </Form.Group>
      );
    }

    if (campo.tipo === "date") {
      return (
        <Form.Group key={campo.slug} className="mt-3">
          <Form.Label><strong>{campo.label || campo.slug}</strong></Form.Label>
          {campo.texto && <div className="text-muted mb-2">{campo.texto}</div>}
          <Form.Control
            type="date"
            value={valor || ""}
            onChange={(e) => handleRespostaDinamicaChange(campo.slug, e.target.value)}
          />
          {campo.observacao && <div className="mt-2 text-muted small">{campo.observacao}</div>}
        </Form.Group>
      );
    }

    return null;
  };

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header>
        <div>
          <Modal.Title>
            {currentTermo
              ? `Aceite Obrigatório: ${currentTermo.titulo} (v${currentTermo.versao})`
              : "Termos Pendentes"}
            {termos.length > 1 &&
              ` (${currentTermoIndex + 1} de ${termos.length})`}
          </Modal.Title>
          {/* indicada por dois médicos, ela vê o mesmo termo duas vezes: sem dizer de quem
              é cada um, não há como distinguir e ela acha que é repetição */}
          {currentTermo?.indicacao_medico && (
            <div className="text-muted small mt-1">
              Referente à indicação de <strong>{currentTermo.indicacao_medico}</strong>
              {currentTermo.indicacao_programa ? ` — ${currentTermo.indicacao_programa}` : ""}
            </div>
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {/* Visualizador de PDF */}
        {currentTermo?.arquivo_pdf_url ? (
          <div
            ref={pdfContainerRef}
            style={{
              height: "60vh",
              overflowY: "auto",
              border: "1px solid #ccc",
            }}
          >
            <embed
              src={currentTermo.arquivo_pdf_url}
              type="application/pdf"
              width="100%"
              height="100%"
            />
            <p className="p-3 text-center">
              Se o PDF não carregar, por favor{" "}
              <a
                href={currentTermo.arquivo_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                clique aqui para abri-lo
              </a>
              .
            </p>
          </div>
        ) : (
          <div className="text-center p-5">
            <Spinner animation="border" />
          </div>
        )}

        {/* Checkbox de Aceite */}
        <Form.Check
          type="checkbox"
          id={`accept-termo-${currentTermo?.id}`}
          label={labelAceiteTermos || "Li e aceito os termos apresentados acima."}
          checked={acceptedCurrent}
          onChange={(e) => setAcceptedCurrent(e.target.checked)}
          className="mt-3"
        />

        {/* Termos dinâmicos do médico — exibidos junto ao último termo, salvos no cadastro do médico */}
        {isUltimoTermo && temTermosDinamicos && (
          <div className="mt-4 pt-3 border-top">
            <h6 className="mb-2">{termosDinamicosTitulo || "Consentimentos do Médico"}</h6>
            {termosDinamicos.map((campo) => renderTermoDinamico(campo))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" onClick={onDecline} disabled={isLoading}>
          Recusar e Sair
        </Button>
        <Button
          variant="success"
          onClick={handleAcceptCurrent}
          disabled={isLoading || !acceptedCurrent || !currentTermo}
        >
          {isLoading ? (
            <Spinner as="span" animation="border" size="sm" />
          ) : (
            "Aceitar e Continuar"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

TermosAcceptModal.propTypes = {
  show: PropTypes.bool.isRequired,
  termos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      titulo: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      versao: PropTypes.number.isRequired,
      arquivo_pdf_url: PropTypes.string,
    })
  ).isRequired,
  userId: PropTypes.number,
  onAccept: PropTypes.func.isRequired,
  onDecline: PropTypes.func.isRequired,
  termosDinamicos: PropTypes.array,
  prestadorUuid: PropTypes.string,
  programaUuid: PropTypes.string,
  respostasIniciais: PropTypes.object,
  labelAceiteTermos: PropTypes.string,
};

export default TermosAcceptModal;
